import { supabase } from './supabase';

/**
 * Inclusive calendar days in past_due (day 1 = anchor UTC date). Days 1–4: banner + full app;
 * day 5+ (`> PLATFORM_PAST_DUE_GRACE_INCLUSIVE_DAYS`): billing screen only until paid.
 */
export const PLATFORM_PAST_DUE_GRACE_INCLUSIVE_DAYS = 4;

function normalizedSubscriptionStatus(status: string | null | undefined): string {
  if (status == null || typeof status !== 'string') return '';
  return status.trim().toLowerCase();
}

/**
 * UTC calendar-day boundaries: 0 = same UTC day as `iso`, 1 = next UTC day, etc.
 * `iso` should be `businesses.subscription_past_due_at`, aligned to Stripe invoice/subscription
 * billing (written by stripe-webhook and sync-platform-subscriptions), not “when we first noticed”.
 */
export function utcCalendarDaysElapsedSince(iso: string | null | undefined): number | null {
  if (iso == null || typeof iso !== 'string' || !iso.trim()) return null;
  const start = new Date(iso.trim());
  if (Number.isNaN(start.getTime())) return null;
  const s = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
  const n = new Date();
  const e = Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate());
  const diff = Math.floor((e - s) / 86400000);
  return diff < 0 ? 0 : diff;
}

/** Day 1 = first UTC calendar day of `subscription_past_due_at`; null if anchor missing. */
export function inclusivePastDueDayNumber(
  subscriptionPastDueAt: string | null | undefined,
): number | null {
  const elapsed = utcCalendarDaysElapsedSince(subscriptionPastDueAt);
  if (elapsed === null) return null;
  return elapsed + 1;
}

/** True when past_due has exceeded grace — block main app, send to billing. */
export function pastDueBillingLockRequired(
  subscriptionPastDueAt: string | null | undefined,
): boolean {
  const n = inclusivePastDueDayNumber(subscriptionPastDueAt);
  if (n === null) return false;
  return n > PLATFORM_PAST_DUE_GRACE_INCLUSIVE_DAYS;
}

/**
 * Platform subscription row values synced from Stripe (see stripe-webhook).
 * Used for guards and the past-due billing banner — keep in sync with webhook statusMap.
 * For `past_due`, pass `subscription_past_due_at` so days 5+ lock out the main app.
 */
export function stripeSyncedStatusAllowsNexusMainApp(
  status: string | null | undefined,
  subscriptionPastDueAt?: string | null,
): boolean {
  const s = normalizedSubscriptionStatus(status);
  if (s === 'active' || s === 'trialing') return true;
  if (s === 'past_due') {
    return !pastDueBillingLockRequired(subscriptionPastDueAt);
  }
  return false;
}

/** Red banner in main app: only days 1–4 of past_due (grace). */
export function stripeSyncedPastDueShowsBillingBanner(
  status: string | null | undefined,
  subscriptionPastDueAt?: string | null,
): boolean {
  if (normalizedSubscriptionStatus(status) !== 'past_due') return false;
  return !pastDueBillingLockRequired(subscriptionPastDueAt);
}

/** Platform Stripe subscription exists and needs payment (not a new-plan signup). */
export function businessNeedsExistingSubscriptionBillingRecovery(
  subscriptionStatus: string | null | undefined,
  stripeSubscriptionId: string | null | undefined,
): boolean {
  if (stripeSubscriptionId == null || typeof stripeSubscriptionId !== 'string') return false;
  if (!stripeSubscriptionId.trim()) return false;
  const s = String(subscriptionStatus || '')
    .trim()
    .toLowerCase();
  return s === 'past_due';
}

export interface BusinessSubscription {
  id: string;
  business_id: string;
  subscription_status: 'active' | 'inactive' | 'trialing' | 'past_due' | 'canceled';
  subscription_last_checked: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  interval: 'monthly' | 'yearly';
  features: string[];
}

// Mock subscription plans (will be replaced with Stripe later)
export const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    id: 'essential',
    name: 'Essential Plan',
    price: 129.99,
    interval: 'monthly',
    features: [
      'Unlimited bookings',
      'Team management',
      'Customer management',
      'Basic reporting',
      'Mobile app access'
    ]
  },
  {
    id: 'professional',
    name: 'Professional Plan',
    price: 249.99,
    interval: 'monthly',
    features: [
      'Everything in Essential',
      'Advanced reporting',
      'Automations',
      'API access',
      'Priority support'
    ]
  }
];

/**
 * Get business subscription status
 */
export async function getBusinessSubscription(businessId: string): Promise<BusinessSubscription | null> {
  try {
    const { data, error } = await supabase
      .from('businesses')
      .select(`
        id,
        subscription_status,
        subscription_last_checked,
        stripe_customer_id,
        stripe_subscription_id,
        created_at,
        updated_at
      `)
      .eq('id', businessId)
      .single();

    if (error) {
      console.error('Error fetching business subscription:', error);
      return null;
    }

    return data as BusinessSubscription;
  } catch (error) {
    console.error('Error in getBusinessSubscription:', error);
    return null;
  }
}

/**
 * Update business subscription status
 */
export async function updateBusinessSubscription(
  businessId: string, 
  status: BusinessSubscription['subscription_status'],
  stripeCustomerId?: string,
  stripeSubscriptionId?: string
): Promise<boolean> {
  try {
    const updateData: any = {
      subscription_status: status,
      subscription_last_checked: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    if (stripeCustomerId) {
      updateData.stripe_customer_id = stripeCustomerId;
    }

    if (stripeSubscriptionId) {
      updateData.stripe_subscription_id = stripeSubscriptionId;
    }

    const { error } = await supabase
      .from('businesses')
      .update(updateData)
      .eq('id', businessId);

    if (error) {
      console.error('Error updating business subscription:', error);
      return false;
    }

    // Cascade update to resources
    await cascadeSubscriptionUpdate(businessId, status);

    return true;
  } catch (error) {
    console.error('Error in updateBusinessSubscription:', error);
    return false;
  }
}

/**
 * Cascade subscription status to all resources for a business
 */
async function cascadeSubscriptionUpdate(businessId: string, status: BusinessSubscription['subscription_status']): Promise<void> {
  try {
    const { error } = await supabase
      .from('resources')
      .update({ 
        subscription_status: status,
        updated_at: new Date().toISOString()
      })
      .eq('business_id', businessId);

    if (error) {
      console.error('Error cascading subscription update to resources:', error);
    }
  } catch (error) {
    console.error('Error in cascadeSubscriptionUpdate:', error);
  }
}

/**
 * Check if business has active subscription
 */
export async function isBusinessSubscriptionActive(businessId: string): Promise<boolean> {
  try {
    const subscription = await getBusinessSubscription(businessId);
    return subscription?.subscription_status === 'active' || subscription?.subscription_status === 'trialing';
  } catch (error) {
    console.error('Error checking business subscription status:', error);
    return false;
  }
}

/**
 * Get all subscription plans
 */
export function getSubscriptionPlans(): SubscriptionPlan[] {
  return SUBSCRIPTION_PLANS;
}

/**
 * Mock function to activate subscription (will be replaced with Stripe)
 */
export async function activateSubscription(businessId: string, planId: string): Promise<boolean> {
  try {
    console.log(`Activating subscription for business ${businessId} with plan ${planId}`);
    
    // Mock activation - in real implementation this would create Stripe subscription
    const success = await updateBusinessSubscription(businessId, 'active');
    
    if (success) {
      console.log(`Successfully activated subscription for business ${businessId}`);
    }
    
    return success;
  } catch (error) {
    console.error('Error activating subscription:', error);
    return false;
  }
}

/**
 * Mock function to deactivate subscription (will be replaced with Stripe)
 */
export async function deactivateSubscription(businessId: string): Promise<boolean> {
  try {
    console.log(`Deactivating subscription for business ${businessId}`);
    
    const success = await updateBusinessSubscription(businessId, 'inactive');
    
    if (success) {
      console.log(`Successfully deactivated subscription for business ${businessId}`);
    }
    
    return success;
  } catch (error) {
    console.error('Error deactivating subscription:', error);
    return false;
  }
} 